import React, { useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Download, CheckCircle2, AlertCircle, X, Image as ImageIcon, ArrowRight, Sparkles } from "lucide-react";

/*
  Bulk Product Import (Admin)
  Workflow:
    1) Upload many product images (multi-file). Returns per-file URLs.
    2) (Optional) Auto-inject URLs into CSV rows by matching filename prefix vs SKU / slug.
    3) Download the CSV template, fill it, then upload/paste CSV.
    4) Preview: server-side validation + duplicate detection. Row status per row.
    5) Import: server inserts non-duplicate/valid rows.
*/

// ---- CSV parsing (RFC-4180-ish, handles quoted fields with commas) ----
function parseCSV(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQ = false;
  const s = text.replace(/\r\n?/g, "\n");
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQ) {
      if (c === '"' && s[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') { inQ = false; }
      else { field += c; }
    } else {
      if (c === '"') { inQ = true; }
      else if (c === ",") { row.push(field); field = ""; }
      else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
      else { field += c; }
    }
  }
  if (field.length > 0 || row.length) { row.push(field); rows.push(row); }
  if (!rows.length) return [];
  const headers = rows[0].map((h) => h.trim());
  return rows.slice(1).filter((r) => r.some((v) => (v || "").trim() !== "")).map((r) => {
    const o = {};
    headers.forEach((h, i) => { o[h] = (r[i] ?? "").trim(); });
    return o;
  });
}

const TEMPLATE_HEADERS = [
  "sku","name","slug","category","subcategory","material","unit_label",
  "price","mrp","stock","tags","short_description","description",
  "care_instructions","package_contents",
  "image_1","image_2","image_3","image_4","image_5","image_6","image_7","image_8",
  "variants",
  "seo_title","seo_description","seo_keywords","image_alt",
  "is_best_seller","is_new_arrival","is_active",
];

const TEMPLATE_EXAMPLE = [
  "AD-PVL-DEMO","Peacock Villaku Demo","peacock-villaku-demo",
  "pooja-items","Peacock Villaku","Antique German Silver","per Pair",
  "1999","2499","20","peacock villaku,pooja lamp,adiyogi",
  "Premium heavy-weight peacock deepam pair","Handcrafted premium peacock villaku in antique German silver finish. Sold as a pair.",
  "Wipe with a soft dry cloth","1 Pair (2 pieces)",
  "https://example.com/peacock-1.jpg","https://example.com/peacock-2.jpg","","","","","","",
  "12 inch:1999:2499:5:PVL-12|14 inch:2499:2999:5:PVL-14|18 inch:2999:3499:5:PVL-18",
  "Peacock Villaku Demo Pair","Premium peacock villaku pair for pooja","peacock villaku,pooja lamp",
  "Premium Peacock Villaku Pair","TRUE","TRUE","TRUE",
];

function toCSV(rows) {
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return rows.map((r) => r.map(esc).join(",")).join("\n");
}

const STATUS_STYLES = {
  ok: "bg-emerald-50 border-emerald-300 text-emerald-800",
  error: "bg-rose-50 border-rose-300 text-rose-800",
  duplicate: "bg-amber-50 border-amber-300 text-amber-900",
};

export default function AdminBulkImport() {
  // --- Step 1: Bulk image uploader ---
  const imgInput = useRef(null);
  const [uploadedImages, setUploadedImages] = useState([]); // [{filename, url}]
  const [uploading, setUploading] = useState(false);

  // --- Step 2/3: CSV ---
  const csvInput = useRef(null);
  const [rawCsv, setRawCsv] = useState("");
  const [parsedRows, setParsedRows] = useState([]); // dict rows
  const [preview, setPreview] = useState(null); // { summary, items }
  const [autoMap, setAutoMap] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const templateCsv = useMemo(() => toCSV([TEMPLATE_HEADERS, TEMPLATE_EXAMPLE]), []);
  const downloadTemplate = () => {
    const blob = new Blob([templateCsv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "adiyogi-bulk-import-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // ------ Handlers: Image upload ------
  const handleImageFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      const { data } = await api.post("/admin/bulk-upload/images", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const good = (data.files || []).filter((f) => f.url).map((f) => ({ filename: f.filename, url: f.url }));
      const bad = (data.files || []).filter((f) => !f.url);
      setUploadedImages((prev) => [...prev, ...good]);
      toast.success(`Uploaded ${good.length} image(s)${bad.length ? ` (${bad.length} failed)` : ""}`);
    } catch (e) {
      toast.error("Image upload failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      if (imgInput.current) imgInput.current.value = "";
    }
  };

  // ------ Auto-map images into rows by SKU/slug prefix in filename ------
  const autoMapImagesIntoRows = (rows) => {
    if (!autoMap || !uploadedImages.length) return rows;
    return rows.map((row) => {
      const keys = [row.sku, row.slug, row.name]
        .filter(Boolean)
        .map((k) => String(k).toLowerCase().replace(/\s+/g, "-"));
      const matches = uploadedImages.filter((img) => {
        const fn = (img.filename || "").toLowerCase();
        return keys.some((k) => k && fn.includes(k));
      });
      if (!matches.length) return row;
      // Sort by numeric suffix so peacock-1.jpg comes before peacock-2.jpg
      matches.sort((a, b) => (a.filename || "").localeCompare(b.filename || ""));
      const out = { ...row };
      matches.slice(0, 10).forEach((m, i) => {
        const key = `image_${i + 1}`;
        if (!(out[key] || "").trim()) out[key] = m.url;
      });
      return out;
    });
  };

  // ------ CSV parse ------
  const parseAndSet = (text) => {
    setRawCsv(text);
    const rows = parseCSV(text);
    const mapped = autoMapImagesIntoRows(rows);
    setParsedRows(mapped);
    setPreview(null);
    setImportResult(null);
    toast.info(`${rows.length} row(s) parsed`);
  };
  const handleCsvFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    parseAndSet(text);
  };

  const runPreview = async () => {
    if (!parsedRows.length) { toast.error("No rows to preview"); return; }
    try {
      const { data } = await api.post("/admin/bulk-import/preview", { rows: parsedRows });
      setPreview(data);
      toast.success(`Preview ready · ${data.summary.ok} ok · ${data.summary.errors} errors · ${data.summary.duplicates} duplicates`);
    } catch (e) {
      toast.error("Preview failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const runImport = async () => {
    if (!parsedRows.length) return;
    if (!window.confirm("Import all valid, non-duplicate rows now?")) return;
    setImporting(true);
    try {
      const { data } = await api.post("/admin/bulk-import/commit", { rows: parsedRows });
      setImportResult(data);
      toast.success(`Imported ${data.counts.inserted} product(s), skipped ${data.counts.skipped}`);
    } catch (e) {
      toast.error("Import failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="max-w-5xl" data-testid="admin-bulk-import">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">Admin · Bulk Tools</div>
        <h1 className="font-serif text-3xl md:text-4xl">Bulk Product Import</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Add 20–30+ products at once. Upload many product images, fill a CSV using the template, preview validation & duplicates, then import in one click. Existing products are never modified.
        </p>
      </div>

      {/* STEP 1 — IMAGES */}
      <section className="border hairline rounded-xl bg-white p-6 mb-6" data-testid="step-1-images">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] gold-text mb-1">Step 1 · Optional</div>
            <div className="font-serif text-xl">Upload product images</div>
            <div className="text-xs text-muted-foreground mt-1">Pick many files at once. Each image gets a shareable URL. Name files with the SKU / slug (e.g. <code>peacock-villaku-1.jpg</code>) so they auto-attach to matching rows.</div>
          </div>
          <button onClick={() => imgInput.current?.click()} disabled={uploading} className="btn-gold rounded-full px-6 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 whitespace-nowrap" data-testid="upload-images-btn">
            <Upload className="w-4 h-4" strokeWidth={1.5} /> {uploading ? "Uploading…" : "Select Images"}
          </button>
          <input ref={imgInput} type="file" multiple accept="image/*" className="hidden" onChange={(e) => handleImageFiles(e.target.files)} data-testid="upload-images-input" />
        </div>
        {uploadedImages.length > 0 && (
          <>
            <div className="text-xs text-muted-foreground mb-2">{uploadedImages.length} uploaded · auto-map by filename prefix is <label className="inline-flex items-center gap-1 ml-1 cursor-pointer"><input type="checkbox" checked={autoMap} onChange={(e) => setAutoMap(e.target.checked)} data-testid="automap-toggle" /> {autoMap ? "ON" : "OFF"}</label></div>
            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-8 gap-2 max-h-64 overflow-y-auto p-1" data-testid="uploaded-images-grid">
              {uploadedImages.map((img, i) => (
                <div key={i} className="relative group border hairline rounded overflow-hidden aspect-square bg-muted">
                  <img src={img.url} alt={img.filename} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-black/60 text-white text-[10px] p-1 overflow-hidden break-all transition">{img.filename}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* STEP 2 — TEMPLATE */}
      <section className="border hairline rounded-xl bg-white p-6 mb-6" data-testid="step-2-template">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] gold-text mb-1">Step 2</div>
            <div className="font-serif text-xl">Download CSV template</div>
            <div className="text-xs text-muted-foreground mt-2 max-w-xl">
              The template includes 30 columns and one example row. For products with multiple sizes, use the <code className="bg-muted px-1 rounded">variants</code> column with the format:
              <br />
              <code className="text-[11px] block mt-1 bg-muted p-2 rounded">size:price:mrp:stock:sku | size:price:mrp:stock:sku</code>
              Example: <code className="text-[11px]">12 inch:1999:2499:5:PVL-12 | 14 inch:2499:2999:5:PVL-14</code>
            </div>
          </div>
          <button onClick={downloadTemplate} className="btn-outline-gold rounded-full px-6 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 whitespace-nowrap" data-testid="download-template-btn">
            <Download className="w-4 h-4" strokeWidth={1.5} /> Download Template
          </button>
        </div>
      </section>

      {/* STEP 3 — CSV UPLOAD */}
      <section className="border hairline rounded-xl bg-white p-6 mb-6" data-testid="step-3-csv">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] gold-text mb-1">Step 3</div>
            <div className="font-serif text-xl">Upload your CSV</div>
            <div className="text-xs text-muted-foreground mt-1">Or paste it below. Every row = one product.</div>
          </div>
          <button onClick={() => csvInput.current?.click()} className="btn-outline-gold rounded-full px-6 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 whitespace-nowrap" data-testid="upload-csv-btn">
            <FileText className="w-4 h-4" strokeWidth={1.5} /> Select CSV
          </button>
          <input ref={csvInput} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => handleCsvFile(e.target.files?.[0])} data-testid="upload-csv-input" />
        </div>
        <textarea
          value={rawCsv}
          onChange={(e) => parseAndSet(e.target.value)}
          placeholder="Paste CSV here (or use Select CSV above)…"
          rows={8}
          className="w-full font-mono text-[11px] border hairline rounded-lg p-3 outline-none"
          data-testid="csv-textarea"
        />
        {parsedRows.length > 0 && (
          <div className="mt-3 flex items-center justify-between">
            <div className="text-xs text-muted-foreground">{parsedRows.length} row(s) parsed{autoMap && uploadedImages.length ? ` · images auto-mapped by filename` : ""}.</div>
            <button onClick={runPreview} className="btn-gold rounded-full px-6 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2" data-testid="preview-btn">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} /> Validate &amp; Preview
            </button>
          </div>
        )}
      </section>

      {/* STEP 4 — PREVIEW */}
      {preview && (
        <section className="border hairline rounded-xl bg-white p-6 mb-6" data-testid="step-4-preview">
          <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] gold-text mb-1">Step 4 · Preview</div>
              <div className="font-serif text-xl">Review before import</div>
              <div className="flex gap-4 mt-2 text-xs">
                <span className="text-emerald-700"><CheckCircle2 className="inline w-3.5 h-3.5 mr-1" strokeWidth={1.5} />{preview.summary.ok} ready</span>
                <span className="text-rose-700"><AlertCircle className="inline w-3.5 h-3.5 mr-1" strokeWidth={1.5} />{preview.summary.errors} errors</span>
                <span className="text-amber-700"><X className="inline w-3.5 h-3.5 mr-1" strokeWidth={1.5} />{preview.summary.duplicates} duplicates</span>
              </div>
            </div>
            {preview.summary.ok > 0 && (
              <button onClick={runImport} disabled={importing} className="btn-gold rounded-full px-7 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2" data-testid="import-btn">
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} /> {importing ? "Importing…" : `Import ${preview.summary.ok} product${preview.summary.ok === 1 ? "" : "s"}`}
              </button>
            )}
          </div>
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-xs" data-testid="preview-table">
              <thead className="text-left text-muted-foreground uppercase tracking-widest text-[10px] sticky top-0 bg-white border-b hairline">
                <tr><th className="py-2 pr-2">#</th><th className="py-2 pr-2">Status</th><th className="py-2 pr-2">Name</th><th className="py-2 pr-2">Category</th><th className="py-2 pr-2">Price</th><th className="py-2 pr-2">MRP</th><th className="py-2 pr-2">Variants</th><th className="py-2 pr-2">Images</th><th className="py-2 pr-2">Notes</th></tr>
              </thead>
              <tbody>
                {preview.items.map((it) => {
                  const p = it.product || {};
                  return (
                    <tr key={it.row_index} className={`border-b hairline ${it.status !== "ok" ? "opacity-90" : ""}`} data-testid={`preview-row-${it.row_index}`}>
                      <td className="py-2 pr-2 align-top">{it.row_index + 1}</td>
                      <td className="py-2 pr-2 align-top"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-widest ${STATUS_STYLES[it.status] || ""}`}>{it.status}</span></td>
                      <td className="py-2 pr-2 align-top">
                        <div className="font-medium">{p.name || "—"}</div>
                        <div className="text-[10px] text-muted-foreground">{p.sku || "no-sku"} · {p.slug || "—"}</div>
                      </td>
                      <td className="py-2 pr-2 align-top">{p.category || "—"}<div className="text-[10px] text-muted-foreground">{p.subcategory}</div></td>
                      <td className="py-2 pr-2 align-top">{p.price ? `₹${p.price}` : "—"}</td>
                      <td className="py-2 pr-2 align-top text-muted-foreground">{p.mrp ? `₹${p.mrp}` : "—"}</td>
                      <td className="py-2 pr-2 align-top">
                        {p.size_options && Object.keys(p.size_options).length > 0 ? (
                          <div className="space-y-0.5">
                            {Object.entries(p.size_options).map(([k, v]) => (<div key={k} className="text-[10px]">{k} · ₹{v}</div>))}
                          </div>
                        ) : <span className="text-[10px] text-muted-foreground">—</span>}
                      </td>
                      <td className="py-2 pr-2 align-top">
                        {(p.images || []).length > 0 ? (
                          <div className="flex gap-1"><span className="inline-flex items-center gap-1 text-[10px]"><ImageIcon className="w-3 h-3" strokeWidth={1.5} />{p.images.length}</span>
                            <div className="flex gap-1">{p.images.slice(0, 3).map((u, i) => (<img key={i} src={u} className="w-8 h-8 rounded object-cover border hairline" alt="" />))}</div>
                          </div>
                        ) : <span className="text-[10px] text-rose-600">none</span>}
                      </td>
                      <td className="py-2 pr-2 align-top">
                        {it.errors?.length > 0 && <div className="text-rose-700 text-[10px]">{it.errors.join("; ")}</div>}
                        {it.duplicate_of && <div className="text-amber-700 text-[10px]">{it.duplicate_of}</div>}
                        {it.warnings?.length > 0 && <div className="text-muted-foreground text-[10px]">{it.warnings.join("; ")}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* STEP 5 — RESULT */}
      {importResult && (
        <section className="border hairline rounded-xl bg-white p-6" data-testid="step-5-result">
          <div className="font-serif text-xl mb-3">Import complete</div>
          <div className="text-sm text-muted-foreground mb-3">
            Inserted <span className="text-emerald-700 font-medium">{importResult.counts.inserted}</span>, skipped <span className="text-amber-700 font-medium">{importResult.counts.skipped}</span>.
          </div>
          {importResult.inserted?.length > 0 && (
            <details className="mb-2" open>
              <summary className="cursor-pointer text-sm font-medium">Inserted ({importResult.inserted.length})</summary>
              <ul className="text-xs mt-2 space-y-0.5 max-h-40 overflow-y-auto">
                {importResult.inserted.map((r) => (<li key={r.product_id}><a href={`/product/${r.slug}`} target="_blank" rel="noreferrer" className="gold-text hover:underline">{r.name}</a> <span className="text-muted-foreground">({r.slug})</span></li>))}
              </ul>
            </details>
          )}
          {importResult.skipped?.length > 0 && (
            <details>
              <summary className="cursor-pointer text-sm font-medium">Skipped ({importResult.skipped.length})</summary>
              <ul className="text-xs mt-2 space-y-0.5 max-h-40 overflow-y-auto">
                {importResult.skipped.map((r, i) => (<li key={i}>Row {r.row_index + 1}: <span className="text-muted-foreground">{r.reason}</span></li>))}
              </ul>
            </details>
          )}
        </section>
      )}
    </div>
  );
}
